import React, { useCallback, useMemo } from 'react';
import {
  Paper,
  Table,
  Badge,
  Text,
  Group,
  ActionIcon,
  Tooltip,
  Stack,
  Center,
  Loader,
  Pagination,
  Box,
  Button,
  Checkbox
} from '@mantine/core';
import {
  IconBuildingStore,
  IconEye,
  IconEdit,
  IconPlayerPause,
  IconPlayerPlay,
  IconWorld,
  IconPhone,
  IconMail,
  IconSearch,
  IconPlus,
  IconBuilding
} from '@tabler/icons-react';
import { BusinessListItemDto } from '../../../../domain/entities/Business';

interface BusinessesListProps {
  // Data
  businesses: BusinessListItemDto[];
  totalCount: number;
  totalPages: number;

  // Loading states
  isLoading: boolean;
  error: Error | null;

  // Pagination
  pageNumber: number;
  onPageChange: (page: number) => void;

  // Actions
  onViewBusiness: (business: BusinessListItemDto) => void;
  onEditBusiness: (business: BusinessListItemDto) => void;
  onDeactivateBusiness: (business: BusinessListItemDto) => void;
  onReactivateBusiness: (business: BusinessListItemDto) => void;
  onCreateBusiness: () => void;

  // Search state (for empty states)
  searchQuery: string;
  onClearSearch: () => void;

  // Sprint 053: Selection props (optional)
  selectedIds?: Set<string>;
  onToggleSelection?: (id: string) => void;
  onSelectAllPage?: () => void;
  isAllPageSelected?: boolean;
}

/**
 * BusinessesList - Table display for businesses with scoped loading states
 * Sprint 045: Renamed delete → deactivate, added reactivate, added Status column
 * Sprint 053: Added selection checkboxes and Company column
 */
export const BusinessesList: React.FC<BusinessesListProps> = ({
  businesses,
  totalCount,
  totalPages,
  isLoading,
  error,
  pageNumber,
  onPageChange,
  onViewBusiness,
  onEditBusiness,
  onDeactivateBusiness,
  onReactivateBusiness,
  onCreateBusiness,
  searchQuery,
  onClearSearch,
  selectedIds,
  onToggleSelection,
  onSelectAllPage,
  isAllPageSelected
}): React.ReactElement => {
  const selectionEnabled = selectedIds !== undefined && onToggleSelection !== undefined;

  const isIndeterminate = useMemo((): boolean => {
    if (!selectedIds || businesses.length === 0) return false;
    const selectedOnPage = businesses.filter(b => selectedIds.has(b.id)).length;
    return selectedOnPage > 0 && selectedOnPage < businesses.length;
  }, [selectedIds, businesses]);

  const handleToggle = useCallback((id: string): void => {
    if (onToggleSelection) {
      onToggleSelection(id);
    }
  }, [onToggleSelection]);

  const handleSelectAll = useCallback((): void => {
    if (onSelectAllPage) {
      onSelectAllPage();
    }
  }, [onSelectAllPage]);

  if (isLoading) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Center py="xl">
          <Stack gap="md" align="center">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">Loading businesses...</Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Center py="xl">
          <Stack gap="md" align="center">
            <Text size="lg" fw={600} c="red">
              Error Loading Businesses
            </Text>
            <Text size="sm" c="dimmed">
              {error.message}
            </Text>
          </Stack>
        </Center>
      </Paper>
    );
  }

  if (totalCount === 0 && searchQuery) {
    return (
      <Paper shadow="sm" p="xl" radius="md" withBorder>
        <Stack align="center" gap="md">
          <IconSearch size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
          <Box ta="center">
            <Text size="lg" fw={600} mb="xs">
              No Businesses Found
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              No businesses match your search query &ldquo;{searchQuery}&rdquo;.
            </Text>
            <Button variant="subtle" onClick={onClearSearch}>
              Clear Search
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  }

  if (totalCount === 0 && !searchQuery) {
    return (
      <Paper shadow="sm" p="xl" radius="md" withBorder>
        <Stack align="center" gap="md">
          <IconBuildingStore size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
          <Box ta="center">
            <Text size="lg" fw={600} mb="xs">
              No Businesses Found
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              Create your first business to start tracking mentions.
            </Text>
            <Button leftSection={<IconPlus size={16} />} onClick={onCreateBusiness}>
              Create First Business
            </Button>
          </Box>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper shadow="sm" p="md" radius="md" withBorder>
      <Table highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            {selectionEnabled && (
              <Table.Th w={40}>
                <Checkbox
                  checked={isAllPageSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  aria-label="Select all businesses on this page"
                />
              </Table.Th>
            )}
            <Table.Th>Business Name</Table.Th>
            <Table.Th>Company</Table.Th>
            <Table.Th>Category</Table.Th>
            <Table.Th>Region</Table.Th>
            <Table.Th>SubArea</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Contact Info</Table.Th>
            <Table.Th>Address</Table.Th>
            <Table.Th>Mentions</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {businesses.map((business) => (
            <Table.Tr key={business.id}>
              {/* Selection Checkbox */}
              {selectionEnabled && (
                <Table.Td>
                  <Checkbox
                    checked={selectedIds.has(business.id)}
                    onChange={(): void => handleToggle(business.id)}
                    aria-label={`Select ${business.displayName}`}
                  />
                </Table.Td>
              )}

              {/* Business Name */}
              <Table.Td>
                <Group gap="xs">
                  <IconBuildingStore size={16} color="var(--mantine-color-gray-6)" />
                  <Text fw={600}>{business.displayName}</Text>
                </Group>
                {business.description && (
                  <Text size="xs" c="dimmed" mt={4} lineClamp={1}>
                    {business.description}
                  </Text>
                )}
              </Table.Td>

              {/* Company */}
              <Table.Td>
                {business.companyName ? (
                  <Group gap={4}>
                    <IconBuilding size={14} color="var(--mantine-color-teal-6)" />
                    <Text size="sm" lineClamp={1}>{business.companyName}</Text>
                  </Group>
                ) : (
                  <Text size="xs" c="dimmed">&mdash;</Text>
                )}
              </Table.Td>

              {/* Category */}
              <Table.Td>
                <Badge variant="light" color="blue">
                  {business.category ?? 'Uncategorized'}
                </Badge>
              </Table.Td>

              {/* Region */}
              <Table.Td>
                <Text size="sm">{business.regionName}</Text>
              </Table.Td>

              {/* SubArea */}
              <Table.Td>
                {business.subAreaName ? (
                  <Badge variant="light" color="cyan" size="sm">
                    {business.subAreaName}
                  </Badge>
                ) : (
                  <Text size="xs" c="dimmed">
                    No SubArea
                  </Text>
                )}
              </Table.Td>

              {/* Status */}
              <Table.Td>
                <Badge variant="light" color={business.isActive ? 'green' : 'gray'}>
                  {business.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </Table.Td>

              {/* Contact Info Icons */}
              <Table.Td>
                <Group gap="xs">
                  {business.website && (
                    <Tooltip label="Website">
                      <ActionIcon
                        variant="light"
                        color="blue"
                        size="sm"
                        component="a"
                        href={business.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <IconWorld size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {business.phone && (
                    <Tooltip label={business.phone}>
                      <ActionIcon
                        variant="light"
                        color="green"
                        size="sm"
                        component="a"
                        href={`tel:${business.phone}`}
                      >
                        <IconPhone size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {business.email && (
                    <Tooltip label={business.email}>
                      <ActionIcon
                        variant="light"
                        color="orange"
                        size="sm"
                        component="a"
                        href={`mailto:${business.email}`}
                      >
                        <IconMail size={16} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {!business.website && !business.phone && !business.email && (
                    <Text size="xs" c="dimmed">
                      No contact info
                    </Text>
                  )}
                </Group>
              </Table.Td>

              {/* Address */}
              <Table.Td>
                {business.formattedAddress ? (
                  <Text size="sm" lineClamp={2}>
                    {business.formattedAddress}
                  </Text>
                ) : business.addrStreet || business.addrCity ? (
                  <Text size="sm" lineClamp={2}>
                    {[business.addrStreet, business.addrCity, business.addrState]
                      .filter(Boolean)
                      .join(', ')}
                  </Text>
                ) : (
                  <Text size="xs" c="dimmed">
                    No address
                  </Text>
                )}
              </Table.Td>

              {/* Mentions Count */}
              <Table.Td>
                <Badge variant="light" color={business.mentionCount > 0 ? 'violet' : 'gray'}>
                  {business.mentionCount} mention{business.mentionCount !== 1 ? 's' : ''}
                </Badge>
              </Table.Td>

              {/* Actions */}
              <Table.Td>
                <Group gap="xs">
                  <Tooltip label="View Details">
                    <ActionIcon variant="light" color="blue" onClick={(): void => onViewBusiness(business)}>
                      <IconEye size={18} />
                    </ActionIcon>
                  </Tooltip>

                  <Tooltip label="Edit Business">
                    <ActionIcon variant="light" color="orange" onClick={(): void => onEditBusiness(business)}>
                      <IconEdit size={18} />
                    </ActionIcon>
                  </Tooltip>

                  {business.isActive ? (
                    <Tooltip label="Deactivate Business">
                      <ActionIcon variant="light" color="red" onClick={(): void => onDeactivateBusiness(business)}>
                        <IconPlayerPause size={18} />
                      </ActionIcon>
                    </Tooltip>
                  ) : (
                    <Tooltip label="Reactivate Business">
                      <ActionIcon variant="light" color="green" onClick={(): void => onReactivateBusiness(business)}>
                        <IconPlayerPlay size={18} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <Center mt="xl">
          <Pagination
            total={totalPages}
            value={pageNumber}
            onChange={onPageChange}
            siblings={1}
            boundaries={1}
          />
        </Center>
      )}
    </Paper>
  );
};
